import { createContext, redirect, type MiddlewareFunction } from "react-router";
import { forget, identify } from "./analytics.js";
import { isApiError, type Api, type Me } from "./api.js";

// Session state for the whole app comes from one control-plane call (/api/me).
// Route middleware fills it once per navigation; loaders and components read it.
export const sessionContext = createContext<Me | null>(null);
export const apiContext = createContext<Api>();

export function signInUrl(next: string): string {
  return `/sign-in?next=${encodeURIComponent(next)}`;
}

// /api/me used to run before every navigation's loaders, so each page switch
// waited out a full round trip before the page's own data even started loading.
// The answer changes only on sign-in/out and membership changes, so cache it and
// revalidate in the background: a navigation reads the cached answer without a
// round trip, and a stale one heals on the next read. Keyed by Api so each test
// (and its fake) stays isolated; `epoch` lets callers without an Api instance
// (the sign-in form) invalidate it.
type Entry = { me: Promise<Me | null>; at: number; epoch: number };
const sessions = new WeakMap<Api, Entry>();
const REVALIDATE_AFTER_MS = 30_000;
let epoch = 0;

/** Force the next read to hit /api/me: sign-in, sign-up, membership changes. */
export function invalidateSession() {
  epoch += 1;
}

async function read(api: Api): Promise<Me | null> {
  try {
    return await api.me();
  } catch (err) {
    if (isApiError(err, "unauthorized")) return null;
    throw err;
  }
}

function store(api: Api): Entry {
  const entry: Entry = { me: read(api), at: Date.now(), epoch };
  // A rejected lookup must not stick, or one offline moment would outlive
  // itself; drop it so the next navigation retries.
  entry.me.catch(() => {
    if (sessions.get(api) === entry) sessions.delete(api);
  });
  sessions.set(api, entry);
  return entry;
}

function loadSession(api: Api): Promise<Me | null> {
  const entry = sessions.get(api);
  if (!entry || entry.epoch !== epoch) return store(api).me;
  // Past the window, refresh in the background but answer from cache now:
  // the navigation must not wait on it.
  if (Date.now() - entry.at > REVALIDATE_AFTER_MS) store(api);
  return entry.me;
}

/** Re-read /api/me now, bypassing the cache. */
export function refreshSession(api: Api): Promise<Me | null> {
  invalidateSession();
  return store(api).me;
}

/** Protected routes: redirect to sign-in (with return path) when unauthenticated. */
export const requireSession: MiddlewareFunction = async ({ request, context }, next) => {
  const api = context.get(apiContext);
  const me = await loadSession(api);
  if (!me) {
    const url = new URL(request.url);
    throw redirect(signInUrl(url.pathname + url.search));
  }
  identify(me);
  context.set(sessionContext, me);
  return next();
};

/** Public auth pages: send signed-in users to the app instead. */
export const redirectIfAuthenticated: MiddlewareFunction = async ({ request, context }, next) => {
  const me = await loadSession(context.get(apiContext));
  if (me) {
    const next = new URL(request.url).searchParams.get("next");
    throw redirect(next && next.startsWith("/") ? next : "/");
  }
  forget();
  return next();
};
